import { useState } from 'react';
import useAsync from 'react-use/lib/useAsync';
import { IconButton, Tooltip, Typography } from '@material-ui/core';
import ViewListIcon from '@material-ui/icons/ViewList';
import ViewModuleIcon from '@material-ui/icons/ViewModule';
import { CATALOG_FILTER_EXISTS } from '@backstage/catalog-client';
import {
  Content,
  ContentHeader,
  HeaderTabs,
  Progress,
} from '@backstage/core-components';
import { createRoutableExtension, useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import {
  DocsCardGrid,
  DocsTable,
  techdocsPlugin,
  TechDocsPageWrapper,
} from '@backstage/plugin-techdocs';
import { docsHomeTabs } from './docsHomeTabs';
import { PsDocsExplorer } from './PsDocsExplorer';

type ViewMode = 'tiles' | 'list';

/**
 * Categorized /docs home. The Professional Services tab shows category tiles
 * linking out to Confluence and Google Drive; the entity tabs show TechDocs
 * entities. Each tab defaults to tiles with a toggle to switch to a list.
 */
const DocsHomeContent = () => {
  const catalogApi = useApi(catalogApiRef);
  const [tabIndex, setTabIndex] = useState(0);
  const [view, setView] = useState<ViewMode>('tiles');

  const { value: entities, loading } = useAsync(async () => {
    const response = await catalogApi.getEntities({
      filter: {
        'metadata.annotations.backstage.io/techdocs-ref': CATALOG_FILTER_EXISTS,
      },
    });
    return response.items;
  }, []);

  const tab = docsHomeTabs[tabIndex];
  const filtered =
    tab.kind === 'entities' ? (entities ?? []).filter(tab.filter) : [];

  return (
    <TechDocsPageWrapper>
      <HeaderTabs
        selectedIndex={tabIndex}
        tabs={docsHomeTabs.map((t, index) => ({
          id: String(index),
          label: t.label,
        }))}
        onChange={index => {
          setTabIndex(index);
          setView('tiles'); // each tab defaults to tiles
        }}
      />
      <Content>
        <ContentHeader title={tab.label}>
          <Tooltip title="Tile view">
            <IconButton
              color={view === 'tiles' ? 'primary' : 'default'}
              onClick={() => setView('tiles')}
              aria-label="Switch to tile view"
            >
              <ViewModuleIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="List view">
            <IconButton
              color={view === 'list' ? 'primary' : 'default'}
              onClick={() => setView('list')}
              aria-label="Switch to list view"
            >
              <ViewListIcon />
            </IconButton>
          </Tooltip>
        </ContentHeader>
        <Typography variant="body1" paragraph>
          {tab.description}
        </Typography>
        {tab.kind === 'ps' && <PsDocsExplorer view={view} />}
        {tab.kind === 'entities' && loading && <Progress />}
        {tab.kind === 'entities' && !loading && view === 'tiles' && (
          <DocsCardGrid entities={filtered} />
        )}
        {tab.kind === 'entities' && !loading && view === 'list' && (
          <DocsTable entities={filtered} title={tab.label} />
        )}
      </Content>
    </TechDocsPageWrapper>
  );
};

/**
 * Routable extension bound to the techdocs index-page route ref. Mounting a
 * plain component at /docs would leave that ref unbound and crash the
 * TechDocs reader header, which links back to the index via useRouteRef.
 */
export const DocsHomePage = techdocsPlugin.provide(
  createRoutableExtension({
    name: 'DocsHomePage',
    component: async () => DocsHomeContent,
    mountPoint: techdocsPlugin.routes.root,
  }),
);
